import axios from 'axios';
import type { AxiosInstance, AxiosResponse } from 'axios';
import { getTokens } from '@rsos/sinatra';
import { getAPIHost } from '@rsos/utils/metaTags';

interface UpdateCallerMetadata {
  facingMode?: 'user' | 'environment';
  isMuted?: boolean;
  isDarkMode?: boolean;
}

interface RoomDetails {
  call_id: string;
  owner_id: string;
  name: string;
}

/**
 * Service client for managing Live Video rooms in the RapidSOS system.
 */
export class LiveVideoService {
  private api: AxiosInstance;
  private useNewService: boolean;

  /**
   * Creates a new instance of the Live Video service.
   * @param orgName The RapidSOS organization name to create the room for. Used for authorization.
   */
  constructor(orgName?: string, useAuth = true, useNewService = false) {
    let token: string | undefined = undefined;
    this.useNewService = useNewService;
    const path = useNewService
      ? '/v1/public-services/video-to-911'
      : '/v1/video_to_911/livevideo';

    if (useAuth) {
      token = getTokens()?.token;
      if (!token) {
        throw new Error('Authentication token is required to create a room.');
      }
    }

    this.api = axios.create({
      baseURL: `${getAPIHost()}${path}`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: useAuth ? `Bearer ${token}` : undefined,
        'X-RapidSOS-Org': orgName,
      },
      withCredentials: useAuth,
    });
  }

  public async dispatcherToken(callID: string): Promise<TokenResponse> {
    const path = this.useNewService ? '/dispatcher-token' : '/dispatcher_token';
    const response = await this.api.post(path, {
      callID,
    });
    this.validateResponse(response, ['token']);
    return response.data;
  }

  public async callerToken(callID: string): Promise<TokenResponse> {
    const path = this.useNewService ? '/caller-token' : '/caller_token';
    const response = await this.api.post(path, {
      callID,
    });
    this.validateResponse(response, ['token']);
    return response.data;
  }

  public async listActiveCalls(): Promise<RoomDetails[]> {
    const response = await this.api.get(`/rooms`);

    this.validateResponse(response, ['rooms']);
    return response.data.rooms;
  }

  public async endRoom(roomName: string): Promise<void> {
    await this.api.delete(`/rooms/${roomName}`);
  }

  public async getCallerToken(token: string): Promise<string> {
    const path = this.useNewService
      ? `/caller-token/${token}`
      : `/caller_token/${token}`;
    const response = await this.api.get(path);
    this.validateResponse(response, ['token']);
    return response.data.token;
  }

  public async updateCallerMetadata(
    roomName: string,
    participantId: string,
    data: UpdateCallerMetadata,
  ): Promise<void> {
    await this.api.patch(
      `/rooms/${roomName}/participants/${participantId}`,
      data,
    );
  }

  private validateResponse(
    response: AxiosResponse<Record<string, unknown>>,
    expectedKeys: string[],
  ) {
    if (!expectedKeys.every(key => key in response.data)) {
      throw new Error(
        `Server response data is missing expected keys: ${expectedKeys.join(', ')}`,
      );
    }
  }
}

export interface TokenResponse {
  token: string;
}
